import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { track } from "../lib/analytics";
import { useTodayLive, liveMode } from "../lib/useTodayLive";
import { useWaitlistStats } from "../lib/useWaitlistStats";
import LastDropCard from "../components/LastDropCard";

/* Season One — 15 consecutive daily drops, 15 Aug → 29 Aug */
const SEASON_ONE = [
  ["#001", "Tech"], ["#002", "Home Essentials"], ["#003", "Kitchen"],
  ["#004", "Grooming"], ["#005", "Fitness"], ["#006", "Audio"],
  ["#007", "Travel"], ["#008", "Wellness"], ["#009", "Desk"],
  ["#010", "Style"], ["#011", "Kids"], ["#012", "Outdoors"],
  ["#013", "Reading"], ["#014", "Craft"], ["#015", "Mega Finale"],
];
/* Drop #001 launches 15 Aug 2026, 21:00 IST → 15:30 UTC */
const DROP_ONE_UTC = "2026-08-15T15:30:00Z";
const DROP_ONE_LABEL = "15 August · 9:00 PM IST";

const FAQ = [
  ["What is Dropshop Live?",
   "India's first daily shopping event. Every night at 9 PM IST, one product goes live for 2 minutes. Watch a live demo, the price drops as more members join, and checkout before the timer ends."],
  ["Why only one product?",
   "Endless browsing is broken. We hand-pick a single product every night so you can decide in two minutes — no scrolling, no fake discounts, no comparison paralysis."],
  ["Why only 250 Founding Members?",
   "We're launching intentionally small. The first 250 members shape the community, unlock the first drops, and keep their Founding badge forever."],
  ["Is joining free?",
   "Yes. Reserving your Founding Member spot is free. You only pay if you buy during a live drop — at the community-unlocked price."],
  ["How do live price drops work?",
   "Every drop has 5 milestones tied to viewer count. As more members arrive, the price crashes through each tier. When the 2-minute live ends, the price is locked and the drop is gone."],
  ["What happens after I become a Founding Member?",
   "You lock in your permanent member number (#1–#250), get early access to every drop, help shape the product, and get direct access to the founding team."],
];

export default function Landing() {
  const { user, admin, loading } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [seasonSlots, setSeasonSlots] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [inviteCode, setInviteCode] = useState(null);
  const [lastBuyable, setLastBuyable] = useState(null);
  const stats = useWaitlistStats();
  const { live: todayLive } = useTodayLive({ intervalMs: 15000 });
  const todayMode = liveMode(todayLive);
  const showTodayCard = ["live", "final", "preshow", "preopen", "today", "upcoming"].includes(todayMode);

  // Capture ?invite=DSL042 on mount, persist across the Google auth redirect
  useEffect(() => {
    const raw = searchParams.get("invite");
    if (raw && /^DSL\d{1,6}$/i.test(raw.trim())) {
      const code = raw.trim().toUpperCase();
      setInviteCode(code);
      try { localStorage.setItem("dsl_invite", code); } catch (_e) { /* ignore */ }
    } else {
      try {
        const stashed = localStorage.getItem("dsl_invite");
        if (stashed) setInviteCode(stashed);
      } catch (_e) { /* ignore */ }
    }
  }, [searchParams]);

  useEffect(() => {
    track("landing_view", { has_invite: !!searchParams.get("invite") });
    api.get("/lives/season").then((r) => setSeasonSlots(r.data?.slots || [])).catch(() => {});
    api.get("/lives/last-buyable").then((r) => setLastBuyable(r.data)).catch(() => {});
    const id = setInterval(
      () => api.get("/lives/last-buyable").then((r) => setLastBuyable(r.data)).catch(() => {}),
      60000, // Refresh every 60s so the 24h window / next-drop cutoff auto-hides the card
    );
    return () => clearInterval(id);
  }, [searchParams]);

  useEffect(() => {
    if (loading) return;
    if (admin || user?.is_founding_member) nav("/home", { replace: true });
    else if (user) nav("/waitlist", { replace: true });
  }, [user, admin, loading, nav]);

  const total = stats?.total_signups ?? 0;
  const cap = stats?.founding_cap ?? 250;
  const spotsLeft = stats?.spots_left ?? 250;
  const pct = Math.min(100, Math.round((total / cap) * 100));
  const openSignup = () => { track("signup_click"); setModalOpen(true); };

  return (
    <div className="min-h-screen bg-[#F8F7F2] text-[#161616]" data-testid="landing-page">
      <TopNav onCta={openSignup} />

      {/* ============== INVITE BANNER (arrives via ?invite=DSL042) ============== */}
      {inviteCode && (
        <div className="border-b border-[#161616] bg-[#B9E641] px-6 md:px-10 py-2.5" data-testid="invite-banner">
          <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-2 font-mono-ds text-[11px] uppercase tracking-[0.24em] text-[#161616]">
            <span>
              <span className="text-[#264733] font-bold">// You&rsquo;ve been invited by</span>{" "}
              <span className="text-[#161616] font-bold" data-testid="invite-banner-code">Member {inviteCode}</span>
            </span>
            <span className="hidden sm:inline text-[#264733]">Founding invite · locks a permanent number</span>
          </div>
        </div>
      )}

      {/* ============== HERO — HOOK SHOT ============== */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-8 md:pb-14" data-testid="landing-hero">
        <div className={`grid gap-8 ${(lastBuyable || showTodayCard) ? "lg:grid-cols-12 lg:items-center" : ""}`}>
          <div className={(lastBuyable || showTodayCard) ? "lg:col-span-7" : ""}>
            <div className="font-mono-ds text-[11px] uppercase tracking-[0.32em] text-[#264733] font-bold" data-testid="landing-folio">
              <span className="inline-block w-2 h-2 bg-[#FF6A00] rounded-full mr-2 align-middle animate-pulse" />
              Tonight · 9:00 PM IST · One product only
            </div>
            <h1
              className={`mt-5 uppercase leading-[0.92] tracking-[-0.02em] font-black ${(lastBuyable || showTodayCard) ? "text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl" : "text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl"} max-w-4xl`}
              style={{ fontFamily: "'Clash Display', 'Newsreader', serif" }}
              data-testid="landing-hero-headline"
            >
              One product. Two minutes.<br />
              <span className="italic font-normal tracking-tight text-[#264733]" style={{ fontFamily: "'Newsreader', Georgia, serif" }}>Then it&rsquo;s gone.</span>
            </h1>
            <p className="mt-6 max-w-xl text-[#161616]/85 text-base md:text-lg leading-relaxed" data-testid="landing-hero-subhead">
              The price crashes as the room fills.
              <span className="text-[#264733] font-semibold"> If you hesitate, you lose.</span>
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                onClick={openSignup}
                className="group relative btn-urgent h-14 px-8 text-[13px] overflow-hidden"
                data-testid="hero-cta-become-founding"
              >
                <span className="inline-block transition-transform duration-200 group-hover:-translate-y-8">Claim a Founding Spot →</span>
                <span className="absolute inset-0 flex items-center justify-center translate-y-8 group-hover:translate-y-0 transition-transform duration-200 font-bold text-[#161616]">
                  Only {spotsLeft} left →
                </span>
              </button>
              <Link to="/login" className="font-mono-ds text-[11px] uppercase tracking-[0.24em] text-[#264733] font-bold hover:text-[#FF6A00] border-b border-[#264733]/40 hover:border-[#FF6A00] pb-0.5" data-testid="hero-cta-signin">
                Already a member? Sign in →
              </Link>
            </div>

            {/* Countdown to Drop #001 — bleed-off pulse variant */}
            {!showTodayCard && <CountdownCard label="Drop #001 launches in" to={DROP_ONE_UTC} sub={DROP_ONE_LABEL} />}
          </div>

          {/* Right column: Last-drop 24h buyable card + today's live card.
              Both sit above the fold so returning visitors see "grab the last
              drop" and "the next drop is X" without scrolling. */}
          {(lastBuyable || showTodayCard) && (
            <div className="lg:col-span-5 flex flex-col gap-4">
              {lastBuyable && (
                <LastDropCard
                  drop={lastBuyable}
                  isAuthed={!!user}
                  onSignInHint={openSignup}
                  defaultAddr={null}
                  compact
                />
              )}
              {showTodayCard && <HeroTodayCard live={todayLive} mode={todayMode} onCta={openSignup} />}
            </div>
          )}
        </div>
      </section>

      {/* ============== SCARCITY DOTS — 250-member canvas ============== */}
      <ScarcityDots total={total} cap={cap} spotsLeft={spotsLeft} onCta={openSignup} />

      {/* ============== FOUNDING MEMBERS COUNTER + BENEFITS ============== */}
      <section className="border-y border-[#161616] bg-[#FFFFFF] px-6 md:px-10 py-16 md:py-20" data-testid="landing-founding">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6">
            <div className="brand-tag mb-4">// Ledger · 001</div>
            <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Founding <span className="text-outline">Members.</span>
            </h2>

            <div className="mt-8 border-2 border-[#161616] bg-[#F8F7F2] shadow-[6px_6px_0_#264733]">
              <div className="flex items-baseline justify-between px-6 pt-5">
                <div>
                  <div className="brand-tiny text-[#6E6E6E]">Members joined</div>
                  <div className="font-display text-5xl md:text-6xl leading-none mt-1 tabular-nums" data-testid="founding-count">
                    {total}<span className="text-[#6E6E6E] text-3xl md:text-4xl"> / {cap}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="brand-tiny text-[#6E6E6E]">Spots left</div>
                  <div className="font-display text-4xl md:text-5xl leading-none mt-1 tabular-nums text-[#FF6A00]" data-testid="founding-spots-left">
                    {spotsLeft}
                  </div>
                </div>
              </div>
              <div className="relative h-3 bg-[#F0EDE4] border-t border-[#E5E1D8] mt-5">
                <div className="absolute inset-y-0 left-0 bg-[#B9E641] transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
              <div className="px-6 py-3 font-mono-ds text-[10px] uppercase tracking-[0.2em] text-[#6E6E6E]">
                Only <span className="text-[#161616] font-bold">{spotsLeft}</span> spots remaining
              </div>
            </div>

            <button onClick={openSignup} className="btn-urgent mt-8 h-14 px-8 text-[13px]" data-testid="founding-cta">
              Reserve My Founding Spot →
            </button>
          </div>

          <ul className="lg:col-span-6 space-y-3 lg:pt-24" data-testid="founding-benefits">
            <BenefitRow n="01" title="Permanent Founding Member Badge" body="Your #001–#250 number is yours for life." />
            <BenefitRow n="02" title="Early access to every Drop" body="Enter live rooms 2 minutes before general availability." />
            <BenefitRow n="03" title="Help shape Dropshop Live" body="Vote on future drops, guide the product roadmap." />
            <BenefitRow n="04" title="Direct access to the founding team" body="Private line to Shubham & the crew." />
          </ul>
        </div>
      </section>

      {/* ============== WHAT IS DROPSHOP LIVE ============== */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="landing-what-is">
        <div className="brand-tag mb-4">// Manifesto · 002</div>
        <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
          Shopping has become <span className="text-outline">overwhelming.</span>
        </h2>
        <div className="mt-8 space-y-5 text-[#161616]/85 text-lg leading-relaxed">
          <p>Too many products. Too many fake discounts. Too much scrolling.</p>
          <p>Dropshop Live brings everyone together for <span className="text-[#264733] font-semibold">one carefully selected product every night.</span></p>
          <p className="font-display text-3xl md:text-4xl italic text-[#264733] tracking-tight leading-tight pt-4">
            No endless browsing. Just one unforgettable shopping moment.
          </p>
        </div>
      </section>

      {/* ============== RITUAL TIMELINE (replaces How It Works) ============== */}
      <RitualTimeline onCta={openSignup} />

      {/* ============== SEASON ONE — BENTO VAULT ============== */}
      <section id="season" className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="landing-season">
        <div className="font-mono-ds text-[11px] uppercase tracking-[0.32em] text-[#264733] font-bold">// The Vault · Season One</div>
        <h2 className="mt-3 uppercase leading-[0.92] tracking-[-0.02em] font-black text-4xl md:text-5xl lg:text-6xl" style={{ fontFamily: "'Clash Display', 'Newsreader', serif" }}>
          15 nights.<br />
          <span className="italic font-normal text-[#264733]" style={{ fontFamily: "'Newsreader', Georgia, serif" }}>14 secrets.</span>
        </h2>
        <p className="mt-4 text-[#6E6E6E] max-w-xl">15 Aug → 29 Aug · one product a night. Classified until 10 AM the day of the drop.</p>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-12 auto-rows-fr gap-3">
          {SEASON_ONE.map(([drop, theme], i) => {
            const dropNo = i + 1;
            const day = 15 + i;
            const isFinale = i === SEASON_ONE.length - 1;
            const slot = (seasonSlots || []).find((s) => s.drop_no === dropNo) || {};
            const liveId = slot.live_id;
            const productName = slot.product;
            const categoryLabel = slot.product_category || theme;
            const revealAt = new Date(Date.UTC(2026, 7, 14 + dropNo, 4, 30, 0));
            const isRevealed = Date.now() >= revealAt.getTime();
            const isCrackedOpen = !isRevealed && (revealAt.getTime() - Date.now()) < 86400000; // <24h
            // Bento asymmetric spans — featured next-drop is 2× wide.
            // Layout math per row (12-col grid):
            //   Row 1: D#001(6) + D#002(3) + D#003(3)  = 12
            //   Row 2: D#004(3) + D#005(3) + D#006(3) + D#007(3) = 12
            //   Row 3: D#008(3) + D#009(3) + D#010(3) + D#011(3) = 12
            //   Row 4: D#012(3) + D#013(3) + D#014(6) = 12
            //   Row 5: D#015 finale(12) = 12
            const span = isFinale
              ? "col-span-2 md:col-span-12"
              : dropNo === 1
                ? "col-span-2 md:col-span-6"
                : dropNo === 14
                  ? "col-span-2 md:col-span-6"
                  : "col-span-1 md:col-span-3";
            return (
              <BentoTile
                key={drop}
                dropNo={dropNo}
                drop={drop}
                day={day}
                isFinale={isFinale}
                isRevealed={isRevealed}
                isCrackedOpen={isCrackedOpen}
                liveId={liveId}
                productName={productName}
                categoryLabel={categoryLabel}
                revealAt={revealAt}
                span={span}
              />
            );
          })}
        </div>
        <div className="mt-10">
          <button onClick={openSignup} className="btn-outline-ns h-12" data-testid="season-cta">Claim Your Number →</button>
        </div>
      </section>

      {/* ============== WHY JOIN ============== */}
      <section className="border-y border-[#161616] bg-[#264733] text-[#F8F7F2] px-6 md:px-10 py-20" data-testid="landing-why">
        <div className="max-w-6xl mx-auto">
          <div className="font-mono-ds text-[11px] uppercase tracking-[0.24em] text-[#B9E641] mb-4">// Why Join · 005</div>
          <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            One product. <span className="italic font-normal text-[#B9E641]">Every night.</span>
          </h2>
          <ul className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              "Live-only prices you won't find elsewhere",
              "One curated product every day",
              "Community unlocks better deals together",
              "Real product demonstrations, not stock photos",
              "Limited-time opportunity — 2 minutes, then gone",
              "A new surprise every single night",
            ].map((t) => (
              <li key={t} className="border border-[#B9E641]/40 px-4 py-4 flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-[#B9E641] mt-2 shrink-0" />
                <span className="text-base md:text-lg leading-snug">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============== FOUNDER ============== */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="landing-founder">
        <div className="brand-tag mb-4">// From the Desk · 006</div>
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-4">
            <div className="aspect-square border-2 border-[#161616] shadow-[6px_6px_0_#264733] overflow-hidden bg-[#F0EDE4] flex items-center justify-center">
              <div className="font-display text-8xl text-[#264733] leading-none">S</div>
            </div>
            <div className="mt-3 font-mono-ds text-[11px] uppercase tracking-[0.24em] text-[#6E6E6E]">
              Shubham · Founder
            </div>
          </div>
          <div className="md:col-span-8 font-display text-2xl md:text-3xl leading-snug tracking-tight">
            &ldquo;Hi, I&rsquo;m Shubham. I&rsquo;m building Dropshop Live because online shopping has become <span className="italic text-[#264733]">overwhelming.</span> I believe shopping should feel exciting again. I&rsquo;m looking for the first <span className="italic text-[#264733]">250 people</span> who want to build this journey with us.&rdquo;
          </div>
        </div>
      </section>

      {/* ============== FAQ ============== */}
      <section id="faq" className="border-y border-[#161616] bg-[#FFFFFF] px-6 md:px-10 py-20" data-testid="landing-faq">
        <div className="max-w-4xl mx-auto">
          <div className="brand-tag mb-4">// FAQ · 007</div>
          <h2 className="font-display uppercase text-4xl md:text-5xl leading-[0.95] tracking-tight mb-8">
            Questions, <span className="text-outline">Answered.</span>
          </h2>
          <div className="border-t border-[#161616]">
            {FAQ.map(([q, a], i) => <FaqItem key={i} idx={i} q={q} a={a} />)}
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center" data-testid="landing-final-cta">
        <h2 className="font-display uppercase text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
          Become one of <br />
          <span className="text-outline">the first 250.</span>
        </h2>
        <p className="mt-6 text-lg md:text-xl text-[#6E6E6E] max-w-xl mx-auto">
          Help us build India&rsquo;s daily shopping ritual.
        </p>
        <button onClick={openSignup} className="btn-urgent mt-10 h-14 px-10 text-[13px]" data-testid="final-cta">
          Become a Founding Member →
        </button>
      </section>

      {/* ============== FOOTER ============== */}
      <footer className="border-t border-[#161616] px-6 md:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-3 font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E]">
        <span>© 2026 · Drop Shop Live · One product a night</span>
        <div className="flex items-center gap-4">
          <a href="#how" className="hover:text-[#FF6A00]">How It Works</a>
          <a href="#season" className="hover:text-[#FF6A00]">Season One</a>
          <a href="#faq" className="hover:text-[#FF6A00]">FAQs</a>
          <Link to="/login" className="hover:text-[#FF6A00]" data-testid="landing-existing-signin">Sign in</Link>
        </div>
      </footer>

      {modalOpen && <SignupModal onClose={() => setModalOpen(false)} onGoogle={() => nav("/login")} spotsLeft={spotsLeft} founding={spotsLeft > 0} inviteCode={inviteCode} />}
    </div>
  );
}

/* ------------ Top nav ------------ */
function TopNav({ onCta }) {
  return (
    <nav className="sticky top-0 z-40 border-b border-[#161616] bg-[#F8F7F2]/95 backdrop-blur" data-testid="landing-nav">
      <div className="max-w-6xl mx-auto flex items-center justify-between px-6 md:px-10 h-16">
        <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
          <div className="w-9 h-9 bg-[#264733] flex items-center justify-center font-display text-white text-xl leading-none shadow-[2px_2px_0_#161616]">D</div>
          <span className="font-display text-lg uppercase tracking-tight">
            Drop Shop <span className="text-[#FF6A00]">Live</span>
          </span>
        </Link>
        <div className="hidden md:flex items-center gap-6 font-mono-ds text-[11px] uppercase tracking-[0.24em] text-[#161616]">
          <a href="#how" className="hover:text-[#FF6A00]" data-testid="nav-how">How It Works</a>
          <a href="#season" className="hover:text-[#FF6A00]" data-testid="nav-season">Season One</a>
          <a href="#faq" className="hover:text-[#FF6A00]" data-testid="nav-faq">FAQs</a>
        </div>
        <button onClick={onCta} className="btn-urgent h-10 px-4 text-[11px]" data-testid="nav-cta-become-founding">
          <span className="hidden sm:inline">Become a Founding Member</span>
          <span className="sm:hidden">Join →</span>
        </button>
        <Link to="/login" className="hidden md:inline font-mono-ds text-[11px] uppercase tracking-[0.24em] text-[#264733] hover:text-[#FF6A00] ml-3" data-testid="nav-signin">
          Sign in →
        </Link>
      </div>
    </nav>
  );
}

/* ------------ Hero Today Card (right-side of Landing hero) ------------
   Shown only when useTodayLive returns a live scheduled for today (or currently
   airing / final window). Compact rich card with product teaser + live status
   pill + countdown chip + join CTA. Clicking navigates to /live/{id} which
   respects the anonymous-preview gate (PreLive shows the lime guest banner). */
function HeroTodayCard({ live, mode, onCta }) {
  const product = live?.product || {};
  const scheduled = live?.scheduled_at ? new Date(live.scheduled_at) : null;
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  // Reveal time = scheduled - 11h (10 AM IST); once passed, product image unblurs
  const revealAt = scheduled ? scheduled.getTime() - 11 * 3600 * 1000 : null;
  const isRevealed = !!(revealAt && now >= revealAt && product.image_url);
  const diff = scheduled ? Math.max(0, scheduled.getTime() - now) : 0;
  const dd = Math.floor(diff / 86400000);
  const hh = Math.floor((diff % 86400000) / 3600000);
  const mm = Math.floor((diff % 3600000) / 60000);
  const ss = Math.floor((diff % 60000) / 1000);
  const compact = dd > 0
    ? `${dd}d ${String(hh).padStart(2,"0")}h`
    : hh > 0 ? `${String(hh).padStart(2,"0")}h ${String(mm).padStart(2,"0")}m` : `${String(mm).padStart(2,"0")}m ${String(ss).padStart(2,"0")}s`;

  const modeMeta = {
    live:     { chip: "● LIVE NOW",       chipCls: "bg-[#FF3B30] text-white",       cta: "Join the room →" },
    final:    { chip: "◆ FINAL WINDOW",   chipCls: "bg-[#FF6A00] text-[#161616]",  cta: "Grab it before it closes →" },
    preshow:  { chip: "◇ DOORS OPEN",     chipCls: "bg-[#B9E641] text-[#161616]",  cta: "Enter the waiting room →" },
    preopen:  { chip: "◇ CHAT OPEN",      chipCls: "bg-[#B9E641] text-[#161616]",  cta: "Enter the waiting room →" },
    today:    { chip: "◇ TONIGHT",        chipCls: "bg-[#161616] text-[#B9E641]",  cta: "Peek inside →" },
    upcoming: { chip: "◇ NEXT DROP",      chipCls: "bg-[#161616] text-[#B9E641]",  cta: "Peek inside →" },
  };
  const meta = modeMeta[mode] || modeMeta.today;
  const cardBase = "relative border-2 border-[#161616] bg-[#FFFFFF] shadow-[6px_6px_0_#FF6A00] overflow-hidden";

  return (
    <div className={cardBase} data-testid="hero-today-card">
      {/* Product teaser — silhouette until reveal, real product image after 10 AM IST */}
      <div className="aspect-video border-b-2 border-[#161616] bg-gradient-to-br from-[#F0EDE4] to-[#E5E1D8] flex items-center justify-center relative overflow-hidden">
        {isRevealed ? (
          <img src={product.image_url} alt={product.name || "Tonight's drop"} className="absolute inset-0 w-full h-full object-cover" data-testid="hero-today-image" />
        ) : (
          <span className="font-display text-8xl text-[#161616]/12 leading-none select-none">?</span>
        )}
        <div className={`absolute top-3 left-3 font-mono-ds text-[10px] uppercase tracking-[0.24em] font-bold px-2 py-1 ${meta.chipCls}`} data-testid="hero-today-chip">
          {meta.chip}
        </div>
        <div className="absolute top-3 right-3 font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#161616] font-bold bg-[#F8F7F2] border border-[#161616] px-2 py-1">
          Drop #{String(live?.drop_no || 1).padStart(3, "0")}
        </div>
      </div>
      <div className="p-5">
        <div className="brand-tiny text-[#6E6E6E]" data-testid="hero-today-date">
          {scheduled ? scheduled.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : ""} IST
        </div>
        <div className="mt-1 font-display text-2xl md:text-3xl uppercase tracking-tight leading-tight" data-testid="hero-today-name">
          {isRevealed ? (product.name || "Tonight's drop") : "Classified · reveals 10 AM IST"}
        </div>
        {product.category && (
          <div className="mt-1 font-mono-ds text-[11px] uppercase tracking-widest text-[#264733] font-bold" data-testid="hero-today-category">
            {product.category}
          </div>
        )}
        {mode !== "live" && mode !== "final" && (
          <div className="mt-4 inline-flex items-center gap-2 border border-[#161616] bg-[#F8F7F2] px-3 h-9 font-mono-ds text-[11px] uppercase tracking-widest">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF6A00] animate-pulse" />
            <span>Starts in <span className="tabular-nums font-bold text-[#FF6A00]">{compact}</span></span>
          </div>
        )}
        {(mode === "live" || mode === "final") && live?.live_id && (
          <Link
            to={`/live/${live.live_id}`}
            onClick={() => track("hero_today_card_click", { live_id: live.live_id, mode })}
            className="mt-4 block btn-urgent h-11 text-center leading-[44px] text-[13px]"
            data-testid="hero-today-cta-live"
          >
            {meta.cta}
          </Link>
        )}
        {(mode === "preshow" || mode === "today" || mode === "upcoming" || mode === "preopen") && live?.live_id && (
          <div className="mt-4 grid grid-cols-2 gap-2 items-stretch">
            <Link
              to={`/live/${live.live_id}`}
              onClick={() => track("hero_today_card_preview", { live_id: live.live_id, mode })}
              className="btn-outline-ns h-11 inline-flex items-center justify-center whitespace-nowrap text-[12px] leading-none !py-0 !px-3"
              data-testid="hero-today-cta-preview"
            >
              Preview →
            </Link>
            <button onClick={onCta} className="btn-urgent h-11 inline-flex items-center justify-center whitespace-nowrap text-[12px] leading-none !py-0 !px-3" data-testid="hero-today-cta-signup">
              Join Founding →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------ Countdown card ------------ */
function CountdownCard({ to, label, sub }) {
  const target = useMemo(() => new Date(to).getTime(), [to]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  const cell = "border border-[#161616] bg-[#FFFFFF] px-4 py-3 min-w-[70px] shadow-[3px_3px_0_#161616] flex flex-col items-center";
  const num = "font-display text-3xl md:text-5xl leading-none tabular-nums";
  const lbl = "font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E] mt-1";
  return (
    <div className="mt-14 max-w-2xl" data-testid="hero-countdown">
      <div className="brand-tiny text-[#6E6E6E] mb-3">{label}</div>
      <div className="flex flex-wrap gap-3">
        <div className={cell}><span className={num}>{String(days).padStart(2,"0")}</span><span className={lbl}>Days</span></div>
        <div className={cell}><span className={num}>{String(hours).padStart(2,"0")}</span><span className={lbl}>Hours</span></div>
        <div className={cell}><span className={num}>{String(mins).padStart(2,"0")}</span><span className={lbl}>Minutes</span></div>
        {/* Seconds cell — pulses on every tick to visualise real-time tension */}
        <div className={`${cell} text-[#FF6A00] relative overflow-hidden`}>
          <span key={secs} className={`${num} inline-block animate-count-pulse`}>{String(secs).padStart(2,"0")}</span>
          <span className={lbl}>Seconds</span>
        </div>
      </div>
      {sub && <div className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E] mt-3">{sub}</div>}
    </div>
  );
}

/* ------------ Scarcity Dots (250 micro-squares) ------------
   250 tiles rendered as a 25×10 grid. Filled tiles = claimed; hollow tiles = open.
   A handful of hollow tiles pulse lime periodically to simulate "someone typing
   their email right now." Pure FOMO — no data lies, no fake counts. */
function ScarcityDots({ total, cap, spotsLeft, onCta }) {
  const [pulseIdxs, setPulseIdxs] = useState([]);
  useEffect(() => {
    const openIdxs = [];
    for (let i = total; i < cap; i += 1) openIdxs.push(i);
    if (openIdxs.length === 0) return undefined;
    const pickTwo = () => {
      const shuffled = openIdxs.slice().sort(() => Math.random() - 0.5).slice(0, 2);
      setPulseIdxs(shuffled);
    };
    pickTwo();
    const id = setInterval(pickTwo, 1200);
    return () => clearInterval(id);
  }, [total, cap]);
  return (
    <section className="bg-[#161616] text-white border-y border-[#161616] px-6 md:px-10 py-16 md:py-24" data-testid="landing-scarcity">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <div className="font-mono-ds text-[11px] uppercase tracking-[0.32em] text-[#FF6A00] font-bold">
            <span className="inline-block w-2 h-2 bg-[#FF6A00] rounded-full mr-2 align-middle animate-pulse" />
            The first 250 are immortalized
          </div>
          <h2 className="mt-5 uppercase leading-[0.9] tracking-[-0.02em] font-black text-4xl md:text-5xl lg:text-6xl" style={{ fontFamily: "'Clash Display', 'Newsreader', serif" }}>
            <span className="tabular-nums text-white" data-testid="scarcity-total">{total}</span> claimed.<br />
            <span className="text-[#FF6A00] tabular-nums" data-testid="scarcity-left">{spotsLeft}</span> remain.
          </h2>
          <p className="mt-3 font-mono-ds text-[10px] uppercase tracking-[0.24em] text-white/40" data-testid="scarcity-note">
            ◇ Doesn&rsquo;t add to {cap}? Three founder seats sit outside the public count.
          </p>
          <p className="mt-6 max-w-md text-white/70 text-base md:text-lg leading-relaxed" style={{ fontFamily: "'Newsreader', Georgia, serif" }}>
            When these {cap} numbers are gone, they are <span className="italic text-[#B9E641]">gone forever.</span> The next cohort waits behind a wall.
          </p>
          <button onClick={onCta} className="mt-8 h-12 px-6 bg-[#B9E641] text-[#161616] font-mono-ds text-[12px] uppercase tracking-[0.24em] font-bold border-2 border-[#B9E641] hover:bg-transparent hover:text-[#B9E641] transition-colors" data-testid="scarcity-cta">
            Lock a number →
          </button>
        </div>
        <div className="lg:col-span-7" data-testid="scarcity-grid">
          <div className="grid grid-cols-25 gap-[3px]" style={{ gridTemplateColumns: "repeat(25, minmax(0, 1fr))" }}>
            {Array.from({ length: cap }, (_, i) => {
              const claimed = i < total;
              const pulsing = pulseIdxs.includes(i);
              return (
                <div
                  key={i}
                  className={`aspect-square ${
                    claimed
                      ? "bg-white"
                      : pulsing
                        ? "bg-[#B9E641] animate-pulse"
                        : "border border-white/25"
                  }`}
                  title={claimed ? `#${String(i + 1).padStart(3, "0")} — claimed` : `#${String(i + 1).padStart(3, "0")} — open`}
                />
              );
            })}
          </div>
          <div className="mt-4 flex items-center gap-4 font-mono-ds text-[10px] uppercase tracking-[0.24em] text-white/60">
            <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 bg-white inline-block" /> Claimed</span>
            <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 border border-white/40 inline-block" /> Open</span>
            <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 bg-[#B9E641] inline-block animate-pulse" /> Right now</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------ Ritual Timeline (replaces How It Works 4-step grid) ------------
   Four emotional beats of every drop day. The 9 PM beat inverts to a dark
   theme to visualise "the lights going out — the room fills — the price falls." */
function RitualTimeline({ onCta }) {
  const beats = [
    { time: "10:00 AM", title: "The Reveal", body: "A single chip unlocks on the Landing. You get to peek at what tonight's drop is — anonymously, no login needed.", tone: "light" },
    { time: "8:58 PM",  title: "The Waiting Room", body: "Doors open. Members start arriving in silence. The countdown ticks past a minute. You feel the tension of the room.", tone: "light" },
    { time: "9:00 PM",  title: "The Drop", body: "Live starts. Two minutes on the clock. As more members join, the price crashes through five tiers. You have to decide before the last hits.", tone: "dark" },
    { time: "9:02 PM",  title: "It's gone.", body: "Room closes. Whoever bought, bought at the price they earned. Whoever hesitated, sees the price on their timeline tomorrow. See you at 10 AM.", tone: "light" },
  ];
  return (
    <section id="how" className="border-y border-[#161616] px-0 py-0" data-testid="landing-ritual">
      <div className="max-w-6xl mx-auto px-6 md:px-10 pt-20 pb-8">
        <div className="font-mono-ds text-[11px] uppercase tracking-[0.32em] text-[#264733] font-bold">// The Ritual · One Day</div>
        <h2 className="mt-5 uppercase leading-[0.9] tracking-[-0.02em] font-black text-4xl md:text-5xl lg:text-6xl" style={{ fontFamily: "'Clash Display', 'Newsreader', serif" }}>
          Every night at 9,<br />
          <span className="italic font-normal text-[#264733]" style={{ fontFamily: "'Newsreader', Georgia, serif" }}>the whole shop opens.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-[#161616]/70 text-lg leading-relaxed">
          One product. One live. One chance. Here&rsquo;s how a Drop Shop day plays out.
        </p>
      </div>
      {beats.map((b, i) => {
        const dark = b.tone === "dark";
        return (
          <div
            key={b.title}
            className={`border-t border-[#161616] px-6 md:px-10 py-12 md:py-16 ${dark ? "bg-[#161616] text-white" : "bg-[#F8F7F2] text-[#161616]"}`}
            data-testid={`ritual-beat-${i}`}
          >
            <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-6 items-start">
              <div className="md:col-span-3">
                <div className={`font-mono-ds text-[11px] uppercase tracking-[0.32em] ${dark ? "text-[#FF6A00]" : "text-[#6E6E6E]"}`}>
                  Step 0{i + 1}
                </div>
                <div className={`mt-2 font-mono-ds text-2xl md:text-3xl tabular-nums font-bold ${dark ? "text-[#B9E641]" : "text-[#264733]"}`}>
                  {b.time}
                </div>
              </div>
              <div className="md:col-span-9">
                <h3
                  className={`uppercase leading-[0.95] tracking-[-0.02em] font-black text-2xl md:text-4xl ${dark ? "text-white" : "text-[#161616]"}`}
                  style={{ fontFamily: "'Clash Display', 'Newsreader', serif" }}
                >
                  {b.title}
                </h3>
                <p
                  className={`mt-3 max-w-2xl text-base md:text-lg leading-relaxed ${dark ? "text-white/80" : "text-[#161616]/80"}`}
                  style={{ fontFamily: "'Newsreader', Georgia, serif" }}
                >
                  {b.body}
                </p>
              </div>
            </div>
          </div>
        );
      })}
      <div className="border-t border-[#161616] bg-[#F8F7F2] px-6 md:px-10 py-14 text-center">
        <button onClick={onCta} className="btn-urgent h-14 px-10 text-[13px]" data-testid="ritual-cta">
          Join tonight&rsquo;s room →
        </button>
      </div>
    </section>
  );
}

/* ------------ Bento Tile (Season One vault card) ------------
   Three visual states:
   - CLASSIFIED   → dark redacted block with vertical "CLASSIFIED" bars + hover static.
                    Not clickable. This is the default for drops that haven't been
                    revealed yet AND aren't within the next 24h.
   - CRACKED OPEN → orange-glow border, "REVEALING" chip. Reveal is imminent
                    (<24h) but not yet passed. Not clickable unless liveId exists.
   - REVEALED     → white/lime tile with product name, clickable Link to PreLive.
*/
function BentoTile({ dropNo, drop, day, isFinale, isRevealed, isCrackedOpen, liveId, productName, categoryLabel, revealAt, span }) {
  const testId = `season-drop-${drop.replace('#','')}`;
  const clickable = !!liveId && (isRevealed || isCrackedOpen);
  const isClassified = !isRevealed && !isCrackedOpen && !isFinale;

  // === Classified redacted tile ===
  if (isClassified) {
    return (
      <div
        className={`${span} group relative overflow-hidden border border-[#161616] bg-[#161616] text-white min-h-[120px] cursor-not-allowed select-none`}
        data-testid={testId}
        title="Classified — revealed at 10 AM IST on the drop day"
      >
        {/* Redaction bars — vertical "CLASSIFIED" text repeated diagonally */}
        <div className="absolute inset-0 flex flex-col justify-center gap-1.5 opacity-60 group-hover:opacity-90 transition-opacity">
          {[0, 1, 2, 3].map((n) => (
            <div key={n} className="font-mono-ds text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-white/40 whitespace-nowrap">
              ██ CLASSIFIED ██ CLASSIFIED ██ CLASSIFIED ██
            </div>
          ))}
        </div>
        {/* Hover: subtle noise band on top for "static reveal" feeling */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity mix-blend-overlay" style={{
          backgroundImage: "repeating-linear-gradient(0deg, rgba(255,106,0,0.5) 0 1px, transparent 1px 3px)",
        }} />
        <div className="relative z-10 p-3 flex justify-between h-full">
          <div className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#B9E641] font-bold">
            Drop {drop}
          </div>
          <div className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-white/60 text-right">
            {day} Aug
          </div>
        </div>
        <div className="absolute bottom-3 left-3 right-3 font-mono-ds text-[9px] uppercase tracking-[0.24em] text-white/40">
          🔒 Reveals 10 AM IST
        </div>
      </div>
    );
  }

  // === Finale (always special) ===
  if (isFinale) {
    return (
      <div
        className={`${span} relative border-2 border-[#161616] bg-[#B9E641] text-[#161616] min-h-[140px] shadow-[6px_6px_0_#161616] p-6 flex flex-col justify-between`}
        data-testid={testId}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="font-mono-ds text-[11px] uppercase tracking-[0.32em] font-bold">◈ MEGA FINALE · 29 Aug</div>
            <div className="mt-3 uppercase leading-[0.95] font-black text-3xl md:text-4xl lg:text-5xl" style={{ fontFamily: "'Clash Display', 'Newsreader', serif" }}>
              The One That<br />
              <span className="italic font-normal" style={{ fontFamily: "'Newsreader', Georgia, serif" }}>Everyone Remembers.</span>
            </div>
          </div>
          <div className="font-display text-6xl md:text-7xl leading-none opacity-80">15</div>
        </div>
        <div className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#264733] font-bold">
          ⚡ 2× longer live · Founding-only doorbust price
        </div>
      </div>
    );
  }

  // === Cracked Open / Revealed tile ===
  const glow = isCrackedOpen ? "border-2 border-[#FF6A00] shadow-[0_0_20px_rgba(255,106,0,0.35),4px_4px_0_#161616]" : "border border-[#161616] shadow-[2px_2px_0_#161616]";
  const featured = dropNo === 1;
  const outerCls = `${span} relative bg-white text-[#161616] min-h-[140px] p-4 flex flex-col gap-2 ${glow} ${clickable ? "cursor-pointer hover:-translate-x-[1px] hover:-translate-y-[1px] transition-transform" : ""}`;
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E]">{day} Aug · 9 PM</div>
          <div className={`mt-1 font-display uppercase tracking-tight leading-none ${featured ? "text-3xl md:text-4xl" : "text-xl"}`}>Drop {drop}</div>
        </div>
        {clickable && <span className="font-mono-ds text-lg text-[#264733]" aria-hidden="true">→</span>}
      </div>
      <div className={`font-mono-ds text-[11px] uppercase tracking-widest text-[#264733] font-bold`}>
        {productName || categoryLabel}
      </div>
      <RevealPill revealAt={revealAt} isFinale={false} productName={productName} dropNo={dropNo} />
      <div className="mt-auto pt-2">
        {isCrackedOpen && (
          <span className="font-mono-ds text-[9px] uppercase tracking-[0.24em] text-[#FF6A00] font-bold">
            ● Cracked open · Peek inside
          </span>
        )}
        {isRevealed && productName && (
          <span className="font-mono-ds text-[9px] uppercase tracking-[0.24em] text-[#264733] font-bold">
            ● Preview →
          </span>
        )}
      </div>
    </>
  );
  return clickable ? (
    <Link to={`/live/${liveId}`} className={outerCls} data-testid={testId} onClick={() => track("landing_drop_preview_click", { dropNo, live_id: liveId })}>
      {inner}
    </Link>
  ) : (
    <div className={outerCls} data-testid={testId}>{inner}</div>
  );
}

/* ------------ Reveal Pill (per-card countdown to 10 AM IST reveal) ------------
   Shows one of three states inside each Season One card:
    - future reveal → animated "REVEALS IN {compact}" chip that live-ticks
    - reveal has passed but no product attached → "◇ REVEALING SOON" muted chip
    - reveal has passed AND product is attached → "⚡ REVEALED" celebratory chip
*/
function RevealPill({ revealAt, isFinale, productName, dropNo }) {
  const target = useMemo(() => revealAt.getTime(), [revealAt]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = target - now;
  const revealed = diff <= 0;

  // Format the countdown into the two most-significant units (Dd Hh, Hh Mm, or Mm Ss)
  const compact = (() => {
    const d = Math.max(0, diff);
    const days = Math.floor(d / 86400000);
    const hours = Math.floor((d % 86400000) / 3600000);
    const mins = Math.floor((d % 3600000) / 60000);
    const secs = Math.floor((d % 60000) / 1000);
    if (days > 0) return `${days}D ${String(hours).padStart(2,"0")}H`;
    if (hours > 0) return `${String(hours).padStart(2,"0")}H ${String(mins).padStart(2,"0")}M`;
    return `${String(mins).padStart(2,"0")}M ${String(secs).padStart(2,"0")}S`;
  })();

  const testId = `season-reveal-${String(dropNo).padStart(3,"0")}`;

  if (revealed && productName) {
    // Reveal has passed AND admin has assigned a product → celebrate
    return (
      <span
        className={`inline-flex items-center gap-1.5 self-start font-mono-ds text-[9px] uppercase tracking-[0.20em] font-bold px-2 py-1 border ${
          isFinale
            ? "border-[#161616] bg-[#161616] text-[#B9E641]"
            : "border-[#264733] bg-[#B9E641] text-[#161616]"
        }`}
        data-testid={testId}
      >
        <span className="text-[#FF6A00]">⚡</span> Revealed
      </span>
    );
  }
  if (revealed) {
    // Reveal has passed but product not yet attached
    return (
      <span
        className={`inline-flex items-center gap-1.5 self-start font-mono-ds text-[9px] uppercase tracking-[0.20em] font-bold px-2 py-1 border border-[#E5E1D8] ${
          isFinale ? "bg-[#B9E641]/50 text-[#161616]" : "bg-[#F8F7F2] text-[#6E6E6E]"
        }`}
        data-testid={testId}
      >
        ◇ Revealing soon
      </span>
    );
  }
  // Future reveal — animated countdown chip
  return (
    <span
      className={`inline-flex items-center gap-1.5 self-start font-mono-ds text-[9px] uppercase tracking-[0.20em] font-bold px-2 py-1 border ${
        isFinale
          ? "border-[#161616] bg-[#161616]/10 text-[#161616]"
          : "border-[#FF6A00]/40 bg-[#FFF3EB] text-[#FF6A00]"
      }`}
      data-testid={testId}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-[#FF6A00] animate-pulse" />
      <span>Reveals in</span>
      <span className="tabular-nums font-black">{compact}</span>
    </span>
  );
}


/* ------------ Benefit row ------------ */
function BenefitRow({ n, title, body }) {
  return (
    <li className="border border-[#161616] bg-[#F8F7F2] p-5 flex items-start gap-4">
      <span className="step-num shrink-0">// {n}</span>
      <div className="min-w-0">
        <div className="font-display text-lg md:text-xl uppercase tracking-tight leading-tight">{title}</div>
        <div className="text-sm text-[#6E6E6E] mt-1">{body}</div>
      </div>
    </li>
  );
}

/* ------------ FAQ accordion item ------------ */
function FaqItem({ idx, q, a }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-[#161616]" data-testid={`faq-item-${idx}`}>
      <button
        className="w-full flex items-center justify-between gap-4 py-5 text-left"
        onClick={() => setOpen((v) => !v)}
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="font-display text-lg md:text-2xl uppercase tracking-tight leading-tight pr-4">{q}</span>
        <span className={`font-mono-ds text-2xl leading-none text-[#264733] transition-transform ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      {open && (
        <div className="pb-5 pr-8 text-[#6E6E6E] leading-relaxed" data-testid={`faq-answer-${idx}`}>{a}</div>
      )}
    </div>
  );
}

/* ------------ Signup modal (Google-only) ------------ */
function SignupModal({ onClose, onGoogle, spotsLeft, founding, inviteCode }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#161616]/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="signup-modal"
    >
      <div className="w-full max-w-lg border-2 border-[#161616] bg-[#FFFFFF] shadow-[8px_8px_0_#264733] p-6 md:p-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="brand-tag mb-1">// The Docket</div>
            <div className="font-display text-2xl md:text-3xl uppercase tracking-tight leading-tight">
              {founding ? "Claim your spot." : "Join the waitlist."}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 border border-[#161616] flex items-center justify-center hover:bg-[#161616] hover:text-white text-xl leading-none" data-testid="signup-close">×</button>
        </div>

        <p className="text-sm text-[#6E6E6E] mb-5">
          Only <span className="text-[#161616] font-bold">{spotsLeft}</span> Founding Members remain. One click with Google and you&apos;re in — we&apos;ll assign your permanent member number instantly.
        </p>

        {inviteCode && (
          <div className="mb-5 border border-[#264733] bg-[#B9E641]/40 px-3 py-2 font-mono-ds text-[11px] uppercase tracking-[0.24em] text-[#264733]" data-testid="signup-invite-note">
            Invited by <span className="text-[#161616] font-bold">Member {inviteCode}</span>
          </div>
        )}

        <button onClick={onGoogle} className="btn-urgent w-full h-12 flex items-center justify-center gap-2" data-testid="signup-google">
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z"/><path fill="currentColor" d="M5.84 14.09a6.61 6.61 0 0 1 0-4.18V7.07H2.18a10.99 10.99 0 0 0 0 9.86l3.66-2.84z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
          Continue with Google →
        </button>

        <p className="mt-4 font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E] text-center">
          // Google · Secure sign-in · No password required
        </p>
      </div>
    </div>
  );
}
